import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import { Loader2 } from "lucide-react";

const BG = "https://images.unsplash.com/photo-1535748020240-f647f81842ee?auto=format&fit=crop&w=1600&h=900&q=80";

export default function DistributorSignup() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", company: "", email: "", password: "" });
  const [agree, setAgree] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const inp = "w-full bg-ink border border-white/10 rounded-md px-4 py-3 outline-none focus:ring-1 focus:ring-brand";

  const submit = async (e) => {
    e.preventDefault();
    if (!agree) { setError("You must accept the Distributor Agreement."); return; }
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/distributor/register", { ...form, agree });
      setUser(data);
      navigate("/distributor");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center px-4 py-24">
      <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-ink/88" />
      <div className="relative z-10 w-full max-w-md bg-surface/90 backdrop-blur-xl border border-white/10 rounded-2xl p-8 animate-fade-up">
        <Logo size="lg" className="mb-4" />
        <h1 className="font-display text-3xl font-black mb-1">Distributor Sign Up</h1>
        <p className="text-white/60 text-sm mb-6">Sell or rent your movies & TV shows on Mandalay+.</p>
        {error && <p data-testid="dist-error" className="bg-brand/15 text-brand text-sm rounded-md px-3 py-2 mb-4">{error}</p>}
        <form onSubmit={submit} className="space-y-4">
          <input data-testid="dist-name" required value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your full name" className={inp} />
          <input data-testid="dist-company" required value={form.company} onChange={(e) => set("company", e.target.value)} placeholder="Company / Studio name" className={inp} />
          <input data-testid="dist-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="Email" className={inp} />
          <input data-testid="dist-password" type="password" required minLength={6} value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="Password (min 6 chars)" className={inp} />
          <label className="flex items-start gap-2 text-xs text-white/60">
            <input data-testid="dist-agree" type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5 accent-brand w-4 h-4" />
            <span>I am the owner or authorised licensor of the content I upload. I grant Mandalay+ rights to host, stream and market it, and I accept the commission schedule (30% / 20% / 10%) and <Link to="/terms" target="_blank" className="text-gold underline">Distributor Terms</Link>. I understand my content is delivered to viewers only by streaming on the Mandalay+ platform and will never be downloadable or distributed off-platform.</span>
          </label>
          <button data-testid="dist-submit" disabled={loading || !agree} className="w-full bg-gold text-black hover:bg-gold/90 transition-colors rounded-md font-semibold py-3 flex items-center justify-center gap-2 disabled:opacity-50">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />} Create Distributor Account
          </button>
        </form>
        <p className="text-sm text-white/60 mt-4">Already a partner? <Link to="/login" className="text-gold">Sign in</Link></p>
      </div>
    </div>
  );
}
